import { ApolloQueryResult } from '@apollo/client';
import { defaultTo } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { SubscriptionScheduleAction } from '@css-xp/graphtypes';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { destroyEntitlement } from '@atlassian/temp-proxies';
import { OMSMetadata } from '@atlassian/cssxp-types-entitlement';
import { DestroyEntitlementRequest, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useTaskList } from '@css-xp/apd-task-manager';
import {
  CancellationPendingLozenge,
  EntitlementIcon,
  ErrorComponents,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { DeleteProductWarningDialog } from './delete-product-warning-dialog';
import './entitlement-cancel-active-subscription-pane.scss';

interface Props {
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}
export interface FormData {
  comments?: string | undefined;
  supportTicketNumber?: string | null;
  handNote?: string | null;
}

export const DeleteProductData = ({ entitlement, transactionAccountContext, refetch }: Props) => {
  const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { closeSidebar } = useSidebar();
  const { id: entitlementId, entitlementNumber } = entitlement;
  const productName = entitlement?.productName || 'Unnamed product';
  const productId = entitlement.productId;
  const isCloud = entitlement.platform?.trim().toUpperCase() === 'CLOUD';
  const isAddOn = entitlement.parentSen ? true : false;
  const { getTransactionAccount, data: transactionAccountData } = transactionAccountContext ?? {};
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const transactionAccount = getTransactionAccount(transactionAccountData);

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Request submitted')
      .withMessage(
        <div>
          The request has been created. You can view your request in the <strong>approval requests</strong> tab. <br />
          <Link to={`#approvals`}>View approval request</Link>
        </div>
      )
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, sendSuccessNotification, setNotificationSent]);

  const { isProductDeleteApprovalFlowEnabled } = useApprovalDeskPermissions();

  const { hasOpenApprovalRequest, loading: taskListLoading, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isProductDeleteApprovalFlowEnabled,
  });

  const {
    approvalList,
    approvalError,
    submitApproval,
    approvalProcessing,
    evaluate: evaluateApproval,
    approvalRequired,
    approvalSlackChannel,
  } = useApprovals({
    approvalType: ApprovalType.PRODUCT_DELETION,
    onApprovalComplete,
  });

  const headerContent = `${isAddOn ? 'AddOn/' : ''}${productName} ${entitlementNumber}`;
  const header = () => {
    return (
      <div className="header-box">
        {!!entitlement?.productId && (
          <EntitlementIcon productId={productId ?? ''} className="avatar medium" isCloud={isCloud} />
        )}
        {headerContent}
        {entitlement.scheduledChanges?.subscriptionScheduleAction === SubscriptionScheduleAction.CANCEL && (
          <CancellationPendingLozenge />
        )}
      </div>
    );
  };

  const { error: formError, isProcessing, submitForm, complete, data: destroyResponseData } = useFormSubmit<
    DestroyEntitlementRequest,
    any
  >({
    submitFormAction: destroyEntitlement,
  });

  useEffect(() => {
    if (complete) {
      // set error
      if (!destroyResponseData?.successful) {
        const errorMessages = [];
        if (destroyResponseData?.failureReason?.EXPEDITE_DESTROY_RESOURCE_PACKAGE_V2?.errorReason) {
          errorMessages.push(destroyResponseData?.failureReason?.EXPEDITE_DESTROY_RESOURCE_PACKAGE_V2?.errorReason);
        }
        if (destroyResponseData?.failureReason?.HAMS_EXPEDITE_DESTROY_RESOURCE_PACKAGE?.errorReason) {
          errorMessages.push(destroyResponseData?.failureReason?.HAMS_EXPEDITE_DESTROY_RESOURCE_PACKAGE?.errorReason);
        }
        setApiError(errorMessages.join(','));
      }
    }
  }, [complete, destroyResponseData]);

  if (complete && destroyResponseData?.successful && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('delete-product-success')
      .withHeader('Data deletion scheduled')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  const constructRequestData = (entitlementId: string, formData: FormData) => {
    const requestData: DestroyEntitlementRequest = {
      entitlementId: entitlementId,
      cloudSiteId: entitlement.cloudSiteId ?? '',
      metadata: {
        [OMSMetadata.SUPPORT_TICKET_NUMBER]: defaultTo(formData?.supportTicketNumber, null),
        [OMSMetadata.HAND_NOTE]: defaultTo(formData?.handNote, null),
      },
    };

    return requestData;
  };
  const handleFormSubmit = async (formData: FormData) => {
    if (!approvalRequired) {
      if (!isProductDeleteApprovalFlowEnabled) {
        setConfirmDialogOpen(true);
        return;
      }
      const evaluation = await evaluateApproval([
        {
          entityType: EntityType.ENTITLEMENT,
          entityId: entitlementId,
          entityNumber: entitlementNumber,
          role: taskListData?.taskList?.userRole,
        },
      ]);
      if (evaluation && !evaluation.isApprovalRequired) {
        setConfirmDialogOpen(true);
      }
      return;
    }
    const requestData = constructRequestData(entitlementId, formData);
    submitApproval({
      approvalType: ApprovalType.PRODUCT_DELETION,
      businessKey: entitlementNumber as string,
      entityInformation: [
        {
          entityType: EntityType.ENTITLEMENT,
          entityId: entitlementId,
          entityNumber: entitlementNumber,
          role: taskListData?.taskList?.userRole,
        },
      ],
      gsacId: formData.supportTicketNumber,
      initiatorComments: formData?.comments,
      additionalInformation: requestData,
    });
  };

  const onConfirmDialog = (formData: FormData) => {
    const requestData = constructRequestData(entitlementId, formData);
    setConfirmDialogOpen(false);
    setApiError(null);
    //@ts-ignore
    submitForm(requestData);
  };
  if (taskListLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, submitting, getState }) => (
          <form {...formProps}>
            <FormHeader title="Delete product data" description={header()} />
            <FormSection>
              {!!taskListError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to fetch the open requests for the following reason"
                      message={taskListError?.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to delete the product data for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
              {!!apiError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to delete the product data for the following reason"
                      message={apiError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {hasOpenApprovalRequest && (
                <InfoPanel
                  type="warning"
                  message={
                    <div className="approval-request-inflight-banner">
                      One or more approval requests are currently in progress.
                      {taskListData?.taskList?.tasks?.length && (
                        <TaskDetailAction
                          task={taskListData.taskList.tasks[0]}
                          text="View approval request details"
                          refetch={refetch}
                          refetchAll={refetch}
                        />
                      )}
                    </div>
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!approvalError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="Unable to delete the product due to the following reason"
                      message={approvalError?.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <InfoPanel
                type="error"
                message={
                  'Once this action is performed, the product data will be scheduled for deletion in 15 days. After that time, the subscription can’t be reactivated and the data can’t be recovered.'
                }
              />
            </FormSection>
            <FormSection>
              <Field name="productId" defaultValue={entitlementNumber} label="Entitlement number">
                {() => <div>{entitlementNumber}</div>}
              </Field>
            </FormSection>
            <FormSection>
              <Field name="linkedBillingAccount" label="Linked Billing Account">
                {() => (
                  <div>
                    <Link to={`${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccount?.transactionAccountNumber}`}>
                      {transactionAccount?.displayName ?? 'Unnamed billing account'}
                      <div>{transactionAccount?.transactionAccountNumber}</div>
                    </Link>
                  </div>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <Field
                name="supportTicketNumber"
                label={'Support ticket number'}
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                isDisabled={!!taskListError || hasOpenApprovalRequest}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      name="supportTicketNumber"
                      placeholder={'Add GSAC ticket number'}
                      testId="support-ticket-number"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>

            <Field
              label={'Internal comment'}
              name="handNote"
              isRequired
              validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              isDisabled={!!taskListError || hasOpenApprovalRequest}
            >
              {({ fieldProps, error }) => (
                <>
                  <Textfield {...fieldProps} name="handNote" testId="hand-note" placeholder={'Add order comments'} />
                  {error && <ErrorMessage testId="supportTicketLink-error">This field is required</ErrorMessage>}
                </>
              )}
            </Field>

            {approvalRequired ? (
              <ApprovalFormSection
                approvalType={ApprovalType.PRODUCT_DELETION}
                slackChannel={approvalSlackChannel ?? ''}
                onCancel={() => closeSidebar()}
                isLoading={approvalProcessing}
                approvalItems={approvalList.filter((item) => item.approvalRequired)}
              />
            ) : (
              <FormFooter align="end">
                <span className="btn-wrapper">
                  <Button
                    appearance="subtle"
                    onClick={() => closeSidebar()}
                    testId="delete-product-close"
                    className="sidebar-btn-mr"
                  >
                    Close
                  </Button>
                  <ButtonGroup>
                    <LoadingButton
                      type="submit"
                      appearance="danger"
                      testId="delete-change-button"
                      isLoading={submitting || isProcessing}
                      isDisabled={!!taskListError || hasOpenApprovalRequest}
                    >
                      Schedule deletion
                    </LoadingButton>
                  </ButtonGroup>
                </span>
              </FormFooter>
            )}
            {isConfirmDialogOpen && (
              <DeleteProductWarningDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => onConfirmDialog(getState().values)}
                entitlement={entitlement}
              />
            )}
          </form>
        )}
      </Form>
    </div>
  );
};

export default DeleteProductData;
